import { Grid, Stack, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { getControlValidationTerms } from 'common/api';
import {
	IFiveResponse,
	IUpdateControlQualityCheckRequest,
} from 'common/api/type-def';
import AIFeedback from 'common/components/AIFeedback/AIFeedback';
import ButtonComp from 'common/components/Button/ButtonComp';
import PromptSelect from 'common/components/PromptSelect/PromptSelect';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { FEEDBACK_OBJECT_TYPE, MODULES } from 'common/constants';
import {
	AI_SIMULATION_MODEL_KEYS,
	CONTROL_5W_LABELS,
	CONTROL_QUALITY_GENERATE_RESULTS,
	CONTROL_QUALITY_HEADER,
	CONTROL_QUALITY_CONTENT_LABEL,
	CONTROL_QUALITY_RESULTS_DETAILS,
	CONTROL_QUALITY_ADDITIONAL_LABEL,
	CONTROL_QUALITY_GENERATE_CONTROL,
	AI_SIMULATION_GENAI_DISCLAIMER,
} from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { setControlQuality } from 'common/store/reducer/simulationReducer';

import {
	get5WControlPromptDetails,
	getControlUpdateDetails,
	getControlUpdatePromptDetails,
} from 'features/AiSimulation/api';

import styles from './ControlQualityInput.module.scss';

interface IControlQualityInput {
	isUpdateControlLoading: boolean;
	handleSave: () => void;
	handleShare: () => void;
	handleDownload: () => void;
	handleLoader: (isLoading: boolean) => void;
}
const ControlQualityInput = (props: IControlQualityInput) => {
	const {
		isUpdateControlLoading,
		handleSave,
		handleShare,
		handleDownload,
		handleLoader,
	} = props;

	const dispatch = useDispatch();

	const [isGenerating, setIsGenerating] = useState(false);

	const { CONTROL_QUALITY_GRAMMAR_CHECK_KEY, CONTROL_LANGUAGE_QUALITY_KEY } =
		AI_SIMULATION_MODEL_KEYS;
	const { OK } = HTTP_STATUS_CODES;
	const { What, When, Where, Who, Why } = CONTROL_5W_LABELS;
	const { AI_SIMULATION_AND_EXPERIMENTATION } = MODULES;
	const { CONTROL_QUALITY_GRAMMAR_CHECK_TYPE } = FEEDBACK_OBJECT_TYPE;
	const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;

	const regulationText = useSelector(
		(state: RootState) => state.simulationArena.regulationText,
	);
	const selectedAIModel = useSelector(
		(state: RootState) => state.simulationArena.selectedAIModel,
	);
	const controlQuality = useSelector(
		(state: RootState) => state.simulationArena.controlQuality,
	);

	const handleTextAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(
			setControlQuality({
				...controlQuality,
				[event.target.name]: event.target.value,
			}),
		);
	};

	const handle5WAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		dispatch(
			setControlQuality({
				...controlQuality,
				five_ws: {
					...(controlQuality.five_ws as IFiveResponse),
					[event.target.name]: event.target.value,
				},
			}),
		);
	};

	const controlUpdatePromptClick = async (event: any) => {
		const promptKey = event.key;
		controlUpdateClicked(promptKey);
	};
	const controlUpdateButtonClick = async () => {
		controlUpdateClicked('');
	};

	const controlUpdateClicked = async (promptKey: string) => {
		handleLoader(true);
		const request: IUpdateControlQualityCheckRequest = {
			control_content: controlQuality.control_content,
			five_ws: controlQuality.five_ws,
			additional_info: controlQuality.additional_info || '',
		};
		const response = promptKey.length
			? await getControlUpdatePromptDetails(promptKey, request)
			: await getControlUpdateDetails(request);
		if (response?.statusCode === OK && response.responseBody) {
			dispatch(
				setControlQuality({
					...controlQuality,
					updatedDescription: response.responseBody.answer,
					controlMapping: [],
				}),
			);
			handleLoader(false);
		} else {
			handleLoader(false);
		}
	};

	const controlQualityPromptClick = async (event: any) => {
		const promptKey = event.key;
		controlQualityCheckClicked(promptKey);
	};
	const controlQualityButtonClick = async () => {
		controlQualityCheckClicked('');
	};

	const controlQualityCheckClicked = async (promptKey: string) => {
		setIsGenerating(true);
		dispatch(
			setControlQuality({
				...controlQuality,
				updatedDescription: '',
				controlMapping: [],
				five_ws: '',
				additional_info: '',
			}),
		);
		const payloadText = controlQuality.control_content;
		const response = promptKey.length
			? await get5WControlPromptDetails(promptKey, payloadText)
			: await getControlValidationTerms(payloadText);
		if (response?.statusCode === OK && response.responseBody) {
			const responseData = response.responseBody as any;

			let output = '';
			try {
				if (promptKey.length) {
					output = JSON.parse(responseData.answer);
				} else {
					output = responseData.fiveWResponse;
				}
			} catch {
				output = '';
			}

			setIsGenerating(false);
			dispatch(
				setControlQuality({
					updatedDescription: '',
					controlMapping: [],
					grammarScore: responseData?.grammarScore || 'NA',
					five_ws: output,
					additional_info: '',
					control_content: controlQuality.control_content,
				}),
			);
		} else {
			setIsGenerating(false);
		}
	};

	useEffect(() => {
		dispatch(
			setControlQuality({
				...controlQuality,
				control_content: regulationText,
			}),
		);
	}, [regulationText]);

	const outputDisplay = controlQuality?.five_ws as IFiveResponse;

	const shouldDisableControlGenerate = () => {
		const obj5W = {
			additionalText: controlQuality.additional_info,
			...outputDisplay,
		};
		return !Object.values(obj5W).some((value) => value);
	};
	return (
		<Grid container className={styles['control-input-wrapper']}>
			<Grid item sm={12}>
				<Typography className={styles['control-header']}>
					{CONTROL_QUALITY_HEADER}
				</Typography>
			</Grid>
			<Grid item sm={12}>
				<Typography className={styles['control-header-input']}>
					{CONTROL_QUALITY_CONTENT_LABEL}
				</Typography>
			</Grid>
			<Grid item sm={12}>
				<TextFieldWithLabel
					inputProps={{
						'data-testid': `quality-input`,
					}}
					id="quality-textarea"
					name="control_content"
					value={controlQuality?.control_content}
					rows={6}
					className={styles.textarea}
					onChange={handleTextAreaChange}
					multiLine
					showLabel
					error={false}
					helperText=""
				/>
			</Grid>
			<Grid item sm={12} className={styles['control-button-generate']}>
				<PromptSelect
					aiModelKey={CONTROL_QUALITY_GRAMMAR_CHECK_KEY}
					btnType="primary"
					onSelectPrompt={controlQualityPromptClick}
					data-testid="btnControlQuality"
					size="small"
					disabled={!controlQuality?.control_content}
					loading={isGenerating}
					clickHandler={controlQualityButtonClick}
					compClass={styles['control-generate']}
					groupClass={styles['control-generate-container']}
				>
					{CONTROL_QUALITY_GENERATE_RESULTS}
				</PromptSelect>
			</Grid>
			{!shouldDisableControlGenerate() && (
				<>
					<Grid item sm={12} className={styles['control-5w-wrapper']}>
						<Grid>
							<Grid item sm={12}>
								<Typography className={styles['control-header-input']}>
									{CONTROL_QUALITY_RESULTS_DETAILS}
								</Typography>
							</Grid>
							<Grid item sm={12}>
								<Typography className={styles['control-output']}>
									{What}
								</Typography>
							</Grid>
							<Grid item sm={12}>
								<TextFieldWithLabel
									inputProps={{
										'data-testid': `quality-input-what`,
									}}
									id="quality-textarea"
									name={What}
									readOnly
									value={outputDisplay?.What}
									className={styles.textarea}
									onChange={handle5WAreaChange}
									multiLine
									showLabel={false}
									error={false}
									helperText=""
								/>
							</Grid>
							<Grid item sm={12}>
								<Typography className={styles['control-output']}>
									{When}
								</Typography>
							</Grid>
							<Grid item sm={12}>
								<TextFieldWithLabel
									inputProps={{
										'data-testid': `quality-input-when`,
									}}
									id="quality-textarea"
									name={When}
									value={outputDisplay?.When}
									className={styles.textarea}
									readOnly
									rows={1}
									onChange={handle5WAreaChange}
									multiLine
									showLabel={false}
									error={false}
									helperText=""
								/>
							</Grid>
							<Grid item sm={12}>
								<Typography className={styles['control-output']}>
									{Where}
								</Typography>
							</Grid>
							<Grid item sm={12}>
								<TextFieldWithLabel
									inputProps={{
										'data-testid': `quality-input-w here`,
									}}
									name={Where}
									id="quality-textarea"
									value={outputDisplay?.Where}
									readOnly
									className={styles.textarea}
									onChange={handle5WAreaChange}
									multiLine
									showLabel={false}
									error={false}
									helperText=""
								/>
							</Grid>
							<Grid item sm={12}>
								<Typography className={styles['control-output']}>
									{Who}
								</Typography>
							</Grid>
							<Grid item sm={12}>
								<TextFieldWithLabel
									inputProps={{
										'data-testid': `quality-input-w ho`,
									}}
									id="quality-textarea"
									name={Who}
									value={outputDisplay?.Who}
									className={styles.textarea}
									rows={2}
									readOnly
									onChange={handle5WAreaChange}
									multiLine
									showLabel={false}
									error={false}
									helperText=""
								/>
							</Grid>
							<Grid item sm={12}>
								<Typography className={styles['control-output']}>
									{Why}
								</Typography>
							</Grid>
							<Grid item sm={12}>
								<TextFieldWithLabel
									inputProps={{
										'data-testid': `quality-input-Why`,
									}}
									id="quality-textarea"
									name={Why}
									value={outputDisplay?.Why}
									className={styles.textarea}
									onChange={handle5WAreaChange}
									multiLine
									readOnly
									showLabel={false}
									error={false}
									helperText=""
								/>
							</Grid>
							<Grid item sm={12}>
								<Stack direction="row" justifyContent="space-between">
									<Typography className={styles['disclaimer-text']}>
										{DISCLAIMER}
									</Typography>
									<AIFeedback
										objectType={CONTROL_QUALITY_GRAMMAR_CHECK_TYPE}
										module={AI_SIMULATION_AND_EXPERIMENTATION}
										objectData={{
											prompt: controlQuality.control_content,
											response: JSON.stringify(controlQuality.five_ws),
											reference: selectedAIModel?.id || '',
										}}
										isDisabled={shouldDisableControlGenerate()}
										shouldResetFeedback={isGenerating}
										allowSaveObjectType
									/>
								</Stack>
							</Grid>
						</Grid>
					</Grid>
					<Grid item sm={12}>
						<Typography className={styles['control-output']}>
							{CONTROL_QUALITY_ADDITIONAL_LABEL}
						</Typography>
					</Grid>
					<Grid item sm={12}>
						<TextFieldWithLabel
							inputProps={{
								'data-testid': `quality-input-additional`,
							}}
							id="quality-textarea"
							value={controlQuality.additional_info}
							name="additional_info"
							className={styles.textarea}
							onChange={handleTextAreaChange}
							multiLine
							showLabel={false}
							error={false}
							helperText=""
						/>
					</Grid>

					<Grid item sm={12} className={styles['control-button-generate']}>
						<PromptSelect
							aiModelKey={CONTROL_LANGUAGE_QUALITY_KEY}
							btnType="primary"
							onSelectPrompt={controlUpdatePromptClick}
							data-testid="btnUpdateControlQuality"
							size="small"
							disabled={shouldDisableControlGenerate()}
							loading={isUpdateControlLoading}
							clickHandler={controlUpdateButtonClick}
							compClass={styles['control-generate']}
							groupClass={styles['control-generate-container']}
						>
							{CONTROL_QUALITY_GENERATE_CONTROL}
						</PromptSelect>
					</Grid>
					<Grid item sm={12}>
						{!!controlQuality?.control_content?.length && (
							<Stack
								direction="row"
								justifyContent="space-around"
								spacing={1}
								className={styles['button-group']}
							>
								<ButtonComp
									key="download-btn"
									btnType="tertiary"
									clickHandler={handleDownload}
									data-testid="download-btn-control"
								>
									Download
								</ButtonComp>
								<ButtonComp
									key="save-btn"
									btnType="tertiary"
									clickHandler={handleSave}
									data-testid="save-btn-control"
								>
									Save
								</ButtonComp>
								<ButtonComp
									key="share-btn"
									btnType="tertiary"
									clickHandler={handleShare}
									data-testid="share-btn-control"
								>
									Share
								</ButtonComp>
							</Stack>
						)}
					</Grid>
				</>
			)}
		</Grid>
	);
};

export default ControlQualityInput;
